<template>
	<div class="cms-social-block" :style="containerStyle">
		<a
			v-for="(link, idx) in activeLinks"
			:key="idx"
			:href="link.url"
			class="cms-social-block__link"
			:class="[
				'cms-social-block__link--' + resolvedStyle,
				'cms-social-block__link--' + size
			]"
			:style="linkStyle(link)"
			target="_blank"
			rel="noopener noreferrer"
			:title="link.label"
		>
			<i :class="'fa fa-' + link.icon"></i>
		</a>
		<div v-if="!activeLinks.length" class="cms-social-block__empty">
			<i class="fa fa-share-alt"></i>
			<span>Add social links via the sidebar</span>
		</div>
	</div>
</template>

<script>
/**
 * SocialBlock.vue
 * ───────────────
 * Renders a row of social media icon links. Each platform can be toggled
 * on/off and has a configurable URL.
 *
 * Props:
 * - links:      JSON string of [{icon, label, url, color}] entries
* - iconStyle:  'plain' | 'filled' | 'outline' — icon button style
 * - size:       'small' | 'medium' | 'large' — icon size
 * - gap:        CSS gap between icons
 * - iconColor:  Override colour for plain style (ignored when filled/outline)
 *
 * Alignment, spacing, background and visibility are wrapper traits — the
 * icon row is inline-flex so the wrapper's text-align positions it.
 */

var DEFAULT_LINKS = [
	{ icon: 'facebook',    label: 'Facebook',  url: '', color: '#1877F2' },
	{ icon: 'twitter',     label: 'Twitter',   url: '', color: '#1DA1F2' },
	{ icon: 'instagram',   label: 'Instagram', url: '', color: '#E4405F' },
	{ icon: 'linkedin',    label: 'LinkedIn',  url: '', color: '#0A66C2' },
	{ icon: 'youtube',     label: 'YouTube',   url: '', color: '#FF0000' },
	{ icon: 'pinterest',   label: 'Pinterest', url: '', color: '#BD081C' },
	{ icon: 'github',      label: 'GitHub',    url: '', color: '#333333' },
	{ icon: 'envelope',    label: 'Email',     url: '', color: '#7f8c99' }
];

export default {
	name: 'SocialBlock',
	inheritAttrs: false,

	props: {
		links: {
			type: String,
			default: function () { return JSON.stringify(DEFAULT_LINKS.slice(0, 4)); }
		},
		iconStyle: {
			type: String,
			default: '',
			validator: function (v) { return !v || ['plain', 'filled', 'outline'].indexOf(v) !== -1; }
		},
		size: {
			type: String,
			default: 'medium',
			validator: function (v) { return ['small', 'medium', 'large'].indexOf(v) !== -1; }
		},
		gap: {
			type: String,
			default: '0.5rem'
		},
		iconColor: {
			type: String,
			default: ''
		}
	},

	computed: {
		/**
		 * Resolves the display style. Prefers the new `iconStyle` prop but
		 * falls back to the legacy `style` key that may still be present in
		 * older saved block data (passed via $attrs since 'style' is reserved).
		 */
		resolvedStyle: function () {
			if (this.iconStyle) return this.iconStyle;
			// Legacy blocks stored the value under 'style'; Vue passes reserved
			// attrs through $attrs rather than as a prop.
			if (this.$attrs && this.$attrs.style && typeof this.$attrs.style === 'string') {
				return this.$attrs.style;
			}
			return 'filled';
		},

		parsedLinks: function () {
			try {
				var result = typeof this.links === 'string' ? JSON.parse(this.links) : this.links;
				return Array.isArray(result) ? result : [];
			} catch (e) {
				return [];
			}
		},

		activeLinks: function () {
			return this.parsedLinks.filter(function (l) { return l && l.url; });
		},

		containerStyle: function () {
			return { gap: this.gap };
		}
	},

	methods: {
		linkStyle: function (link) {
			if (this.resolvedStyle === 'filled') {
				return { backgroundColor: link.color || '#1E2031', color: '#FFF' };
			}
			if (this.resolvedStyle === 'outline') {
				return { borderColor: link.color || '#1E2031', color: link.color || '#1E2031' };
			}
			// plain
			return { color: this.iconColor || link.color || '#1E2031' };
		}
	}
};
</script>

<style scoped>
.cms-social-block {
	display: inline-flex;
	flex-wrap: wrap;
	align-items: center;
	max-width: 100%;
	vertical-align: middle;
}

.cms-social-block__link {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	text-decoration: none;
	transition: opacity 0.15s, transform 0.15s;
	border: 2px solid transparent;
	border-radius: 50%;
}
.cms-social-block__link:hover {
	opacity: 0.8;
	transform: scale(1.1);
}

/* ── Sizes ── */
.cms-social-block__link--small  { width: 32px; height: 32px; font-size: 1em; }
.cms-social-block__link--medium { width: 40px; height: 40px; font-size: 1.15em; }
.cms-social-block__link--large  { width: 52px; height: 52px; font-size: 1.4em; }

/* ── Styles ── */
.cms-social-block__link--filled {
	border-radius: 50%;
}
.cms-social-block__link--outline {
	background: transparent;
	border-radius: 50%;
}
.cms-social-block__link--plain {
	background: transparent;
	border-color: transparent;
	border-radius: 0;
}

/* ── Empty placeholder ── */
.cms-social-block__empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-width: 240px;
	min-height: 60px;
	text-align: center;
	background: #F4F4F5;
	border: 2px dashed #DDDEE0;
	border-radius: 4px;
	color: #999;
	gap: 0.25rem;
	font-size: 1em;
}
.cms-social-block__empty .fa { font-size: 1.4em; }
</style>
